// ตารางเวรพนักงาน แบบ คน × วัน — the roster matrix's pure helpers.
//
// The same employee_timetable rows the calendar view already holds for one
// branch-month, re-cut: a PERSON per row, a DAY per column. That shape is the
// point — the two numbers a manager rosters by fall out of the axes for free.
// A row read across is one person's month (how many shifts they carry); a
// column read down is one day's team (how many people, and of which position).
//
// Everything here is pure. Fetching (including the cross-branch lookup that
// tells us a person is rostered somewhere else that day) lives in crossBranch.js.
import { isLeaveRow } from './clockin.js'
import { positionShort } from './employee.js'
import { isAssistant, ASSIST_SHIFT_TYPE } from './shiftAssist.js'

const pad = (n) => String(n).padStart(2, '0')

/* ── the day columns ─────────────────────────────────────────────────────
 * The MONTH, not the calendar grid: a matrix column is a date, so the leading
 * and trailing days of a 6-week grid would be columns belonging to another
 * month's roster. */
export function monthDays(year, month) {
  const last = new Date(year, month, 0).getDate()
  const out = []
  for (let d = 1; d <= last; d++) {
    const dt = new Date(year, month - 1, d)
    const dow = dt.getDay()
    out.push({
      date: `${year}-${pad(month)}-${pad(d)}`,
      day: d,
      dow,
      weekend: dow === 0 || dow === 6,
    })
  }
  return out
}

// จ อ พ พฤ ศ ส อา — Sunday-first index (Date#getDay), one glyph wide so a
// 31-column header stays readable at ~30px per column.
export const DOW_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']

// A rule down the left of every Saturday and the right of every Sunday, so the
// weekend reads as one bracketed pair. The weekend tint alone is easy to lose
// against a chip's own fill; a line is not, and counting "which week is this"
// across 31 columns is most of what the eye is doing up here.
export const weekEdge = (d) => [
  d?.dow === 6 ? 'border-l border-l-slate-300' : '',
  d?.dow === 0 ? 'border-r border-r-slate-300' : '',
].filter(Boolean).join(' ')

/* ── cell labels ────────────────────────────────────────────────────────
 * A cell is ~30px. The full type never fits, and a truncated "เวรผู้..." reads
 * as nothing, so each type gets a deliberate short form instead. */
export const TYPE_SHORT = {
  'กะเช้า': 'ช',
  'กะบ่าย': 'บ',
  'กะควบ': 'ค',
  'เวรผู้ช่วย': 'ผช',
  'WFH': 'W',
  'Office': 'Off',
  'ลากิจ': 'กิจ',
  'ลาป่วย': 'ป่วย',
  'Leave without pay': 'LWP',
}
export const shortType = (t) => TYPE_SHORT[String(t || '')] || String(t || '').slice(0, 2)

/* ── position grouping ──────────────────────────────────────────────────
 * Roster order, not the HR enum's order: the two positions that decide whether
 * a day works (assistants, then counter) sit at the top, the office roles that
 * are rostered but not chair-side fall to the bottom. Anything unlisted lands
 * just before "no position", so a new enum value is visible rather than hidden.
 */
export const ROSTER_POSITION_ORDER = [
  'ผู้ช่วยทันตแพทย์', 'Counter', 'ผู้จัดการ', 'Admin', 'HR',
  'ช่างทันตกรรม', 'Graphic', 'Sale', 'Messenger', 'แม่บ้าน', 'CEO', 'อื่นๆ',
]
const POSITION_RANK = new Map(ROSTER_POSITION_ORDER.map((p, i) => [p, i]))
export const positionRank = (p) => (POSITION_RANK.has(String(p || '')) ? POSITION_RANK.get(String(p || '')) : ROSTER_POSITION_ORDER.length + (p ? 0 : 1))

/* The type a new shift for this person most likely is. Position decides it —
 * an assistant works เวรผู้ช่วย, a counter works a morning or an afternoon —
 * but what the person ACTUALLY worked this month beats the rule, because that
 * is the one that knows this person is on afternoons. */
export const POSITION_DEFAULT_TYPE = {
  'ผู้ช่วยทันตแพทย์': 'เวรผู้ช่วย',
  'Counter': 'กะเช้า',
  'ผู้จัดการ': 'กะเช้า',
  'แม่บ้าน': 'กะเช้า',
  'Admin': 'Office',
  'HR': 'Office',
  'Graphic': 'Office',
  'Sale': 'Office',
  'CEO': 'Office',
  'ช่างทันตกรรม': 'Office',
  'Messenger': 'Office',
}
const FALLBACK_TYPE = 'เวรผู้ช่วย'

export function predictType(person) {
  const tally = new Map()
  for (const r of person?.rows || []) {
    if (isLeaveRow(r)) continue // ลา says nothing about the shift they would work
    const t = String(r.type || '')
    if (t) tally.set(t, (tally.get(t) || 0) + 1)
  }
  let best = ''
  let bestN = 0
  for (const [t, n] of tally) if (n > bestN) { best = t; bestN = n }
  const guess = best || POSITION_DEFAULT_TYPE[String(person?.employee?.position || '')] || FALLBACK_TYPE
  // Never predict a shift the person is not allowed to be given (below).
  if (guess === ASSIST_SHIFT_TYPE && !canAssist(person?.employee)) return 'กะเช้า'
  return guess
}

/**
 * เวรผู้ช่วย is chair-side work, so it is offered only to staff whose ROLE list
 * carries `Assistance` — not to whoever happens to hold the ผู้ช่วยทันตแพทย์
 * position. The two nearly coincide (every assistant has the role) but the role
 * is the wider and more correct set: a Messenger cross-trained to assist has it
 * too, and gating on position would refuse them a shift they actually work.
 */
export const canAssist = (employee) => isAssistant(employee)
export const typeAllowed = (type, employee) => type !== ASSIST_SHIFT_TYPE || canAssist(employee)

// Who in `people` may NOT be given `type` — the action bar names them rather
// than just greying a chip and leaving the operator to guess who is blocking it.
export function ineligibleFor(type, people) {
  if (type !== ASSIST_SHIFT_TYPE) return []
  return (people || []).filter((p) => !canAssist(p.employee))
}

/* ── the people rows ─────────────────────────────────────────────────────
 * Two sources, unioned:
 *   · everyone whose HOME branch is this one — they belong on the roster even
 *     on a month where nobody has assigned them anything yet, which is exactly
 *     the month this view exists to fill;
 *   · anyone else who already has a row here (a borrowed assistant from another
 *     branch — ~5% of rows), who would otherwise have shifts on screen with no
 *     row to put them on.
 * The second group is marked `home: false` so the view can say where they came
 * from instead of implying they are staff here.
 */
export function rosterPeople({ employees = [], rows = [], branchId, empById }) {
  const bid = Number(branchId) || 0
  const byEmp = new Map()
  for (const r of rows) {
    const id = Number(r?.employee_id) || 0
    if (!id) continue
    if (!byEmp.has(id)) byEmp.set(id, [])
    byEmp.get(id).push(r)
  }

  const ids = new Set()
  for (const e of employees) if (bid && Number(e?.branch_id) === bid) ids.add(Number(e.id))
  for (const id of byEmp.keys()) ids.add(id)

  const out = []
  for (const id of ids) {
    const employee = empById?.get(id) || null
    const list = byEmp.get(id) || []
    out.push({
      id,
      employee,
      // The addon on the row is the fallback: a borrowed employee may be absent
      // from the roster list this page loaded (it is capped at 500 actives).
      code: employee?.code || list[0]?.employee?.code || `#${id}`,
      position: employee?.position || '',
      home: !!employee && Number(employee.branch_id) === bid,
      rows: list,
      byDate: new Map(list.map((r) => [String(r.date || '').slice(0, 10), r])),
    })
  }
  return out
}

// Position groups, each already sorted the way a Thai reader expects.
export function groupPeople(people) {
  const groups = new Map()
  for (const p of people) {
    const key = String(p.position || '')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(p)
  }
  const out = [...groups.entries()].map(([position, list]) => ({
    position,
    label: positionShort(position),
    people: list.sort((a, b) => (
      Number(b.home) - Number(a.home)
      || String(a.code).localeCompare(String(b.code), 'th')
    )),
  }))
  return out.sort((a, b) => positionRank(a.position) - positionRank(b.position))
}

/* ── the two stats ───────────────────────────────────────────────────── */

/**
 * One person's month: shifts worked, the three kinds of leave apart from each
 * other, and the two OT flags.
 *
 * ลากิจ and ลาป่วย are counted separately because they are not the same fact —
 * one is planned and one is not, and a month with five sick days is a
 * conversation a month with five personal days is not. Leave without pay is
 * the third: off the ลากิจ quota, and unpaid.
 *
 * Days OFF are deliberately absent: with one row per (employee, date) enforced
 * across the whole clinic, a day with no row HERE may be a day at another
 * branch, and calling that "หยุด" would be a guess presented as a number.
 */
export const LEAVE_PERSONAL_TYPE = 'ลากิจ'
export const LEAVE_SICK_TYPE = 'ลาป่วย'
export const LEAVE_UNPAID_TYPE = 'Leave without pay'

export function personStats(person) {
  const out = { shifts: 0, leave: 0, personal: 0, sick: 0, unpaid: 0, otAllow: 0, otSpecial: 0 }
  for (const r of person?.rows || []) {
    if (isLeaveRow(r)) {
      out.leave += 1
      if (String(r.type) === LEAVE_SICK_TYPE) out.sick += 1
      else if (String(r.type) === LEAVE_UNPAID_TYPE) out.unpaid += 1
      else out.personal += 1
    } else {
      out.shifts += 1
    }
    // The flags live on the row whatever its type, so they are read from the
    // row rather than inferred from it.
    if (r?.allow_OT === true) out.otAllow += 1
    if (r?.special_OT === true) out.otSpecial += 1
  }
  return out
}

/**
 * One day's team, per date: how many people, split by position.
 *
 * Leave is counted apart and never inside `total` — a ลากิจ row means that
 * person is NOT there, and a headcount that includes them is the one number on
 * this screen that could cause an understaffed day to be signed off.
 */
export function dayStats(rows, empById) {
  const m = new Map()
  for (const r of rows) {
    const date = String(r?.date || '').slice(0, 10)
    if (!date) continue
    if (!m.has(date)) m.set(date, { total: 0, leave: 0, byPosition: new Map() })
    const cell = m.get(date)
    if (isLeaveRow(r)) { cell.leave += 1; continue }
    cell.total += 1
    const pos = empById?.get(Number(r.employee_id))?.position || ''
    cell.byPosition.set(pos, (cell.byPosition.get(pos) || 0) + 1)
  }
  return m
}

// Every position that shows up in the month, in roster order — the footer's
// rows. Driven by the data, so a branch with no ช่างทันตกรรม has no empty row
// for one.
export function positionsPresent(dayStatsMap) {
  const seen = new Set()
  for (const cell of dayStatsMap.values()) for (const p of cell.byPosition.keys()) seen.add(p)
  return [...seen].sort((a, b) => positionRank(a) - positionRank(b))
}

// "ผู้ช่วย 4 · Counter 3 · ผู้จัดการ 1" — the day's composition as one line, for
// the calendar view's day panel and the matrix column tooltip.
export function composition(cell) {
  if (!cell || !cell.total) return ''
  return [...cell.byPosition.entries()]
    .sort((a, b) => positionRank(a[0]) - positionRank(b[0]))
    .map(([p, n]) => `${positionShort(p)} ${n}`)
    .join(' · ')
}

// Doctor shifts per day, from the ทันตแพทย์ roster feed. Context for the
// headcount rather than a rule: the clinic's own assistant-to-doctor ratio runs
// anywhere from 5:3 to 2:4 depending on the branch, so the two numbers are
// shown side by side and the manager judges.
export function doctorsByDay(rows) {
  const m = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    const date = String(r?.date || '').slice(0, 10)
    if (!date) continue
    m.set(date, (m.get(date) || 0) + 1)
  }
  return m
}
